import { inject } from '@angular/core';
import { TaApiService } from '@shared/services';

import { PopoverConfig, PopoverConfigFactory, PopoverHandlerConfig } from '../generic-popover.interfaces';
import { defaultCancelAction, defaultConfirmAction } from './default-actions';
import { taShiftTimeFormConfig } from '@shared/components/edit-create-form/configs/ta-shift-time.form.config';
import { DateHelper } from '@shared/helpers';
import { ApiCallService } from '@shared/services/api-call.service';
import { DialogService } from 'primeng/dynamicdialog';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDeleteWithNoteDialogComponent } from '../dialogs/confirm-delete-with-note.dialog';
import { TaShiftTimeAbsentDialogComponent } from '../dialogs/ta-shift-time-absent.dialog';

export const taShiftTimePopoverConfig: PopoverConfigFactory = (

  onSuccess: () => void,
  onCancel: () => void,
  actionHandlers?: Record<string, (popoverConfig: PopoverHandlerConfig, formValue: any) => void | Promise<any>>
): PopoverConfig => {
  const taApiService = inject(TaApiService);
  const apiCallService = inject(ApiCallService);
  const dialogService = inject(DialogService);
  const confirmationService = inject(ConfirmationService);

  return {
    width: '650px',
    title: 'Chỉnh sửa ca trực',
    dismissable: true,
    styleClass: 'p-fluid',
    formConfig: taShiftTimeFormConfig(),
    actions: [
      defaultCancelAction,
      defaultConfirmAction
    ],
    secondaryActions: [
      {
        label: 'Xóa',
        icon: 'pi pi-trash',
        severity: 'danger',
        onClick: 'delete',
        disabled: (formValue: any) => !formValue?.id // Only show delete for existing items
      },
      {
        label: 'Nghỉ',
        icon: 'pi pi-user-minus',
        severity: 'warn',
        onClick: 'absent',
        disabled: (formValue: any) => !formValue?.id || !formValue.committed
      }
    ],
    actionHandlers: {
      // Built-in delete handler to make popover self-contained
      delete: (popoverConfig, formValue) => {
        const id = formValue?.id;
        if (!id) return;
        const isCommitted = !!formValue?.committed;
        if (!isCommitted) {
          confirmationService.confirm({
            message: 'Bạn có chắc chắn muốn xóa ca trực này không?',
            header: 'Xác nhận xóa',
            icon: 'pi pi-exclamation-triangle',
            closeOnEscape: true,
            dismissableMask: true,
            acceptLabel: 'Xóa',
            rejectLabel: 'Hủy',
            accept: async () => {
              const { success } = await apiCallService.execute(
                taApiService.deleteTaShiftTime(id, {}),
                'Xóa ca trực thành công'
              );
              if (success) popoverConfig?.onSuccess?.();
            },
          });
        } else {
          const ref = dialogService.open(ConfirmDeleteWithNoteDialogComponent, {
            header: 'Xác nhận xóa',
            width: '420px',
            data: { id },
            modal: true,
            closable: true,
            dismissableMask: true,
            closeOnEscape: true,
            appendTo: 'body',
          });
          ref.onClose.subscribe((ok?: boolean) => {
            if (ok) popoverConfig?.onSuccess?.();
          });
        }
      },
      // Open small dialog to collect absent/cover info
      absent: (popoverConfig, formValue) => {
        const ref = dialogService.open(TaShiftTimeAbsentDialogComponent, {
          header: 'Đánh dấu nghỉ',
          width: '520px',
          data: { original: formValue },
          modal: true,
          closable: true,
          dismissableMask: true,
          closeOnEscape: true,
          draggable: true,
          appendTo: 'body'
        });
        ref.onClose.subscribe((ok?: boolean) => {
          if (ok) popoverConfig?.onSuccess?.();
        });
      },
      ...(actionHandlers || {})
    },
    handlerConfig: {
      service: taApiService,
      method: 'createTaShiftTime',
      updateMethod: 'updateTaShiftTime',
      entityLabel: 'ca trực trợ giảng',
      commonDataTransform: (formValue: any) => ({
        taId: formValue.taId,
        centerId: formValue.studySpace === false ? formValue.centerId : null,
        clusterId: formValue.studySpace === true ? formValue.clusterId : null,
        date: formValue.date ? DateHelper.formatDateLocal(formValue.date) : null,
        from: formValue.from ? (formValue.from.getHours() * 60 + formValue.from.getMinutes()) : null,
        to: formValue.to ? (formValue.to.getHours() * 60 + formValue.to.getMinutes()) : null,
        note: formValue?.note || null
      }),
      updateDataTransform: (formValue: any) => ({
        id: formValue.id, // Form field 'id' contains the shiftTimeId value
        // date: formValue.date ? DateHelper.formatDateLocal(formValue.date) : null,
        // from: formValue.from ? (formValue.from.getHours() * 60 + formValue.from.getMinutes()) : null,
        // to: formValue.to ? (formValue.to.getHours() * 60 + formValue.to.getMinutes()) : null
      }),
      initialDataTransform: (rawData: any) => {
        return {
          id: rawData.shiftTimeId,
          taId: rawData.taId,
          date: rawData.date ? new Date(rawData.date) : null,
          studySpace: !!rawData.clusterId, // true if clusterId exists, false if centerId exists
          centerId: rawData.centerId,
          clusterId: rawData.clusterId,
          from: DateHelper.minutesToTime(rawData.from),
          to: DateHelper.minutesToTime(rawData.to),
          committed: !!rawData.committed
        };
      },
      onSuccess,
      onCancel
    }
  };
};
